import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth, TOKEN_KEY, LD_NAZIV_KEY, safeStorage } from "../App";
import { toast } from "sonner";
import { useForceLightMode } from "../hooks/useForceLightMode";
import ScreenshotSlideshow from "../components/ScreenshotSlideshow";
import { DeviceFrame, DeviceSwitcherToolbar } from "../components/DevicePreview";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Target, Calendar, Home, Bell, Users, Map, GraduationCap,
  Phone, Handshake,
  FileText, Award, TrendingUp, CheckCircle2, Sparkles,
  ArrowRight, ShieldCheck, Smartphone, Cloud,
  Sunrise, Navigation, Play
} from "lucide-react";

const FEATURES = [
  { icon: Target, title: "Plan odstrela", desc: "Vodenje kvot, avtomatska SMS obvestila ob zaprtju kategorij." },
  { icon: Calendar, title: "Koledar in dogodki", desc: "Prijave na dogodke, avtomatski arhiv, sledenje prisotnosti." },
  { icon: Home, title: "Rezervacije lovskih koč", desc: "Koledar rezervacij, izvoz v Excel/PDF." },
  { icon: Users, title: "Upravljanje članov", desc: "Uvoz iz Lisjaka, vloge in fino-zrnata dovoljenja, avtomatski preračun ur." },
  { icon: Map, title: "Karta lovišča", desc: "Meje, oznake, kilometrski kamni, žive plasti (vreme, radar, luna)." },
  { icon: Sunrise, title: "Visoke preže", desc: "Uvoz katastra, rezervacije po svitu/mraku, jasen pregled zasedena/prosta v realnem času." },
  { icon: Navigation, title: "Načrtovanje skupnega lova", desc: "Risanje pogonov, stojišča in gonjači, delitev lokacije v realnem času med lovom." },
  { icon: GraduationCap, title: "Izobraževanje pripravnikov", desc: "Do 2 mentorja, naloge, sledenje napredku po urah." },
  { icon: FileText, title: "Datoteke in obvestila", desc: "Skupna dokumentacija, forum in komunikacija." },
  { icon: Award, title: "Odstrel (beležka)", desc: "Beleženje uplenjenih živali (spol, starost, teže) z avtomatiko." },
  { icon: TrendingUp, title: "Ure in delo", desc: "Beleženje delovnih ur, prenosi med člani, potrjevanje." },
];

const LandingPage = () => {
  useForceLightMode();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [demoLoading, setDemoLoading] = useState(null); // "clan" | "predsednik" | null
  const [deviceMode, setDeviceMode] = useState("desktop");
  const [previewShot, setPreviewShot] = useState("dashboard"); // dashboard|karta|plan|dogodki|obvestila

  // Fire "demo_impression" event once when the landing page mounts (GDPR-safe, anonymous — no PII stored)
  useEffect(() => {
    axios.post(`${API}/track/landing-event`, {
      event: "demo_impression",
      page: window.location.pathname,
      referrer: document.referrer || null,
    }).catch(() => {});
  }, []);

  const trackEvent = (event) => {
    axios.post(`${API}/track/landing-event`, {
      event,
      page: window.location.pathname,
      referrer: document.referrer || null,
    }).catch(() => {});
  };

  const startDemo = async (role) => {
    setDemoLoading(role);
    trackEvent(`demo_start_${role}`);
    try {
      const r = await axios.post(`${API}/demo/login`, { role });
      const { token: newToken, user: userData } = r.data;
      safeStorage.setItem(TOKEN_KEY, newToken);
      if (userData?.ld_id) safeStorage.setItem("ld_id", userData.ld_id);
      if (userData?.ld_naziv) safeStorage.setItem(LD_NAZIV_KEY, userData.ld_naziv);
      await login("", "", null, { skipRequest: true, prefetched: { token: newToken, user: userData } });
      toast.success(`Demo vstop kot ${role === "predsednik" ? "predsednik" : "član"}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Napaka pri demo vstopu");
    } finally {
      setDemoLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf7f0]">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-primary text-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/dober-pogled-logo.png" alt="" className="w-11 h-11 rounded-full ring-2 ring-white/30 bg-white p-0.5" />
            <span className="font-bold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>Dober pogled app</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm" className="text-white hover:bg-white/15 hover:text-white" data-testid="nav-login">Prijava</Button></Link>
            <Link to="/register-ld"><Button size="sm" variant="secondary" data-testid="nav-register">Registriraj LD</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative hero-bg text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-10 -left-10 w-72 h-72 bg-amber-200/30 rounded-full blur-3xl" />
          <div className="absolute bottom-10 -right-10 w-96 h-96 bg-white/20 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24 grid md:grid-cols-2 gap-8 items-center">
          <div className="space-y-5">
            <Badge className="bg-amber-400 text-primary border-amber-500 hover:bg-amber-400 font-semibold">
              <Sparkles className="h-3 w-3 mr-1" />1 mesec brezplačno
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              Osrednja platforma za slovenske lovske družine
            </h1>
            <p className="text-lg text-white max-w-xl leading-relaxed">
              Vodenje članov, kvote odstrela, dogodkov, zasedenost lovskih koč, spremljanje izobraževanja pripravnikov in karte lovišča – vse na enem mestu, dostopno na telefonu, tablici in računalniku.
            </p>
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/register-ld"><Button size="lg" variant="secondary" className="gap-2 w-full sm:w-auto font-semibold" data-testid="hero-cta-register">
                  Registriraj svojo LD<ArrowRight className="h-4 w-4" />
                </Button></Link>
                <Link to="/login"><Button size="lg" variant="outline" className="w-full sm:w-auto bg-transparent border-white/40 text-white hover:bg-white/15 hover:text-white" data-testid="hero-cta-login">Prijava</Button></Link>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto bg-amber-300/10 border-amber-300/70 text-amber-100 hover:bg-amber-300/25 hover:text-white gap-2 font-semibold"
                  disabled={demoLoading !== null}
                  onClick={() => startDemo("clan")}
                  data-testid="hero-cta-demo-clan"
                  title="Vstop kot demo član"
                >
                  <Play className="h-4 w-4" />
                  {demoLoading === "clan" ? "Vstopam …" : "Demo: član"}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto bg-amber-300/20 border-amber-300 text-amber-100 hover:bg-amber-300/30 hover:text-white gap-2 font-semibold"
                  disabled={demoLoading !== null}
                  onClick={() => startDemo("predsednik")}
                  data-testid="hero-cta-demo"
                  title="Vstop kot demo predsednik"
                >
                  <Play className="h-4 w-4" />
                  {demoLoading === "predsednik" ? "Vstopam …" : "Demo: predsednik"}
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-6 pt-4 text-sm text-white font-medium">
              <div className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-amber-300" />Brez namestitve</div>
              <div className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-amber-300" />Mobilno</div>
              <div className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-amber-300" />Enotna cena</div>
            </div>
          </div>
          <div className="relative flex justify-center">
            <div className="absolute -top-10 -left-10 w-72 h-72 bg-amber-300/20 rounded-full blur-3xl" />
            <img src="/dober-pogled-logo.png" alt="Dober pogled app logo" className="relative w-72 md:w-96 rounded-full shadow-2xl ring-8 ring-white/95 bg-white" />
          </div>
        </div>
      </section>

      {/* Screenshot slideshow (below hero, above features) */}
      <ScreenshotSlideshow />

      {/* Interaktivni predogled naprave */}
      <section className="max-w-6xl mx-auto px-4 py-12 md:py-16" id="predogled">
        <div className="text-center mb-8">
          <Badge variant="outline" className="mb-3 border-emerald-800 dark:border-emerald-300 text-emerald-900 dark:text-emerald-100">
            PREDOGLED
          </Badge>
          <h2
            className="text-3xl md:text-4xl font-bold text-emerald-900 dark:text-emerald-100"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Poglejte, kako izgleda na vaši napravi
          </h2>
          <p className="text-slate-700 dark:text-slate-300 mt-2">
            Preklopite med mobilnim telefonom, tablico in računalnikom. Za pravo interakcijo kliknite &quot;Preizkusi kot član&quot;.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-4">
          <DeviceSwitcherToolbar
            mode={deviceMode}
            onModeChange={setDeviceMode}
            title="Predogled na"
          />
        </div>
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {[
            { k: "dashboard", label: "Nadzorna plošča" },
            { k: "karta", label: "Karta" },
            { k: "plan", label: "Plan odstrela" },
            { k: "dogodki", label: "Dogodki" },
            { k: "obvestila", label: "Obvestila" },
          ].map((s) => (
            <button
              key={s.k}
              onClick={() => setPreviewShot(s.k)}
              className={`px-3 py-1.5 rounded-full text-xs border transition ${
                previewShot === s.k
                  ? "bg-emerald-900 text-white border-emerald-900"
                  : "bg-white text-emerald-900 border-emerald-900/30 hover:border-emerald-900"
              }`}
              data-testid={`preview-shot-${s.k}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="rounded-3xl bg-gradient-to-b from-emerald-950 to-emerald-900 p-6 md:p-10">
          <DeviceFrame
            src={`/demo?section=${previewShot}`}
            mode={deviceMode}
            fit="auto"
            maxHeight={720}
            isolated
          />
        </div>

        <div className="text-center mt-6">
          <Button
            size="lg"
            className="bg-emerald-900 hover:bg-emerald-800 text-white gap-2"
            disabled={demoLoading !== null}
            onClick={() => startDemo("clan")}
            data-testid="preview-cta-clan"
          >
            <Play className="h-4 w-4" />
            Preizkusi v živo (član)
          </Button>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 py-12 md:py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-emerald-900 dark:text-emerald-100" style={{ fontFamily: "'Playfair Display', serif" }}>Vse, kar potrebuje sodobna LD</h2>
          <p className="text-slate-700 dark:text-slate-300 mt-2">Zasnovano s strani lovca (starešine), prilagojeno slovenskemu lovskemu okolju in (LZS, ZGS, ...).</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <Card key={i} className="border-primary/10 hover:border-primary/30 hover:shadow-md transition" data-testid={`feature-${i}`}>
              <CardContent className="p-5">
                <div className="w-10 h-10 rounded-lg bg-emerald-900/10 dark:bg-emerald-100/10 flex items-center justify-center mb-3">
                  <f.icon className="h-5 w-5 text-emerald-900 dark:text-emerald-100" />
                </div>
                <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 mb-1">{f.title}</h3>
                <p className="text-sm text-slate-800 dark:text-slate-200">{f.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-4xl mx-auto px-4 py-12 md:py-16" id="cenik">
        <div className="text-center mb-10">
          <Badge variant="outline" className="mb-3 border-emerald-800 dark:border-emerald-300 text-emerald-900 dark:text-emerald-100">CENIK</Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-emerald-900 dark:text-emerald-100" style={{ fontFamily: "'Playfair Display', serif" }}>Preprosto, brez presenečenj</h2>
          <p className="text-slate-700 dark:text-slate-300 mt-2">Prvi mesec je brezplačen. Cena velja za celotno lovsko družino – ne na število članov.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Card className="border-2 border-muted" data-testid="price-monthly">
            <CardContent className="p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-lg">Mesečna naročnina</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-5xl font-bold text-emerald-900 dark:text-emerald-100">16€</span>
                  <span className="text-slate-700 dark:text-slate-300">/mesec</span>
                </div>
                <p className="text-xs text-slate-700 dark:text-slate-300 mt-1">zaračunano mesečno</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />Vse funkcije platforme</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />Neomejeno članov</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />Mobilna PWA aplikacija</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />Izvoz Excel/PDF</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />Podpora prek e-pošte</li>
              </ul>
            </CardContent>
          </Card>
          <Card className="border-2 border-emerald-800 dark:border-emerald-400 shadow-xl ring-4 ring-emerald-900/10 dark:ring-emerald-400/20 relative" data-testid="price-yearly">
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-900 dark:bg-emerald-600 text-white">Priporočamo · Prihranek 48€</Badge>
            <CardContent className="p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-1">Letna naročnina <Sparkles className="h-4 w-4 text-emerald-900 dark:text-emerald-100" /></h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-5xl font-bold text-emerald-900 dark:text-emerald-100">12€</span>
                  <span className="text-slate-700 dark:text-slate-300">/mesec</span>
                </div>
                <p className="text-xs text-slate-700 dark:text-slate-300 mt-1">
                  <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">144 € letno</span>
                  <span className="mx-1">·</span>
                  <span>zaračunano 1× letno</span>
                  <span className="mx-1">·</span>
                  <span className="text-green-700 font-semibold">25% ceneje</span>
                </p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />Vse iz mesečne</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" /><strong>25% ceneje</strong> na mesec</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />Prihranek 48€ na leto</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />Zaračunano 1× letno</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />Prednostna podpora</li>
              </ul>
            </CardContent>
          </Card>
        </div>
        <p className="text-xs text-slate-700 dark:text-slate-300 text-center mt-6">
          Prvi mesec brezplačno · Plačilo preko Stripe (kreditna kartica) ali bančnega nakazila · Odpoved kadarkoli
        </p>
      </section>

      {/* Trust */}
      <section className="max-w-4xl mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-6">
            <ShieldCheck className="h-8 w-8 text-emerald-900 dark:text-emerald-200 mx-auto mb-2" />
            <h3 className="font-semibold">GDPR skladno</h3>
            <p className="text-sm text-slate-700 dark:text-slate-300">Podatki so na varnem, ločeni po LD.</p>
          </div>
          <div className="text-center p-6">
            <Smartphone className="h-8 w-8 text-emerald-900 dark:text-emerald-200 mx-auto mb-2" />
            <h3 className="font-semibold">Deluje povsod</h3>
            <p className="text-sm text-slate-700 dark:text-slate-300">Namesti na telefon kot aplikacijo (PWA).</p>
          </div>
          <div className="text-center p-6">
            <Cloud className="h-8 w-8 text-emerald-900 dark:text-emerald-200 mx-auto mb-2" />
            <h3 className="font-semibold">Nič namestitve</h3>
            <p className="text-sm text-slate-700 dark:text-slate-300">Registriraš se in začneš uporabljati.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 py-16 text-center">
        <Card className="bg-gradient-to-br from-emerald-900 to-emerald-800 dark:from-emerald-800 dark:to-emerald-900 text-white border-0 shadow-2xl">
          <CardContent className="p-10 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Želite preizkusiti? Ponujamo vam en mesec brezplačne uporabe.</h2>
            <p className="text-white">Registriraj svojo LD in začni v par minutah – ali <strong>vstopi v demo z enim klikom</strong>.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
              <Link to="/register-ld"><Button size="lg" variant="secondary" className="gap-2" data-testid="cta-register" onClick={() => trackEvent("register_click")}>
                Registriraj LD zdaj<ArrowRight className="h-4 w-4" />
              </Button></Link>
              <Link to="/login"><Button size="lg" variant="ghost" className="text-white hover:bg-white/10">Prijava</Button></Link>
            </div>

            <div className="pt-6 border-t border-white/20 mt-6">
              <p className="text-xs uppercase tracking-wider text-amber-200 mb-3 font-semibold">
                Brez registracije · vstopi v demo takoj
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white gap-2"
                  disabled={demoLoading !== null}
                  onClick={() => startDemo("clan")}
                  data-testid="landing-demo-clan"
                >
                  <Play className="h-4 w-4" />
                  {demoLoading === "clan" ? "Vstopam …" : "Preizkusi kot član"}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white gap-2"
                  disabled={demoLoading !== null}
                  onClick={() => startDemo("predsednik")}
                  data-testid="landing-demo-predsednik"
                >
                  <Play className="h-4 w-4" />
                  {demoLoading === "predsednik" ? "Vstopam …" : "Preizkusi kot predsednik"}
                </Button>
              </div>
              <p className="text-[11px] text-white/70 mt-2">
                Vsi demo podatki so izmišljeni · brez povezovanja z resničnimi LD
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Personal demo / field presentation section */}
      <section className="max-w-5xl mx-auto px-4 py-12" data-testid="section-personal-demo">
        <Card className="bg-amber-50/70 dark:bg-amber-950/40 border-2 border-amber-200/70 dark:border-amber-700/60 shadow-lg overflow-hidden">
          <CardContent className="p-6 sm:p-8">
            <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 items-center">
              <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-emerald-900/10 dark:bg-emerald-100/10 flex items-center justify-center mx-auto md:mx-0">
                <Handshake className="h-9 w-9 sm:h-11 sm:w-11 text-emerald-900 dark:text-emerald-100" />
              </div>
              <div className="text-center md:text-left space-y-2">
                <h3 className="text-xl sm:text-2xl font-bold text-emerald-900 dark:text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Želite predstavitev na terenu?
                </h3>
                <p className="text-slate-800 dark:text-slate-100 text-sm sm:text-base">
                  Z veseljem se oglasim v vaši LD in <strong>osebno predstavim</strong> aplikacijo — na sestanku, v koči ali kar v lovišču.
                  Brez obveznosti, brez pritiska. Pokažem funkcije, odgovorim na vprašanja, skupaj preverimo, ali se aplikacija sklada z vašim delom.
                </p>
                <p className="text-slate-800 dark:text-slate-100 text-sm sm:text-base font-medium">
                  📞 Pokličite ali pošljite SMS/WhatsApp — ter se dogovorimo za termin.
                </p>
              </div>
              <div className="flex flex-col gap-2 items-center md:items-end">
                <a
                  href="tel:+38641350782"
                  onClick={() => trackEvent("phone_click")}
                  className="inline-flex items-center gap-2 bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white px-5 py-3 rounded-lg font-semibold shadow-md transition text-base sm:text-lg whitespace-nowrap"
                  data-testid="cta-call-phone"
                >
                  <Phone className="h-5 w-5" />
                  041 350 782
                </a>
                <a
                  href="mailto:info@doberpogled.app?subject=Terenska%20predstavitev%20Dober%20pogled%20app"
                  onClick={() => trackEvent("email_click")}
                  className="text-xs sm:text-sm text-emerald-900 dark:text-white hover:underline"
                  data-testid="cta-email-demo"
                >
                  ali info@doberpogled.app
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t border-primary/10 mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-slate-700 dark:text-slate-300">
          <div className="flex items-center gap-2">
            <img src="/dober-pogled-logo.png" alt="" className="w-6 h-6 rounded-full" />
            <span>© 2026 Dober pogled app · Platforma za slovenske lovske družine</span>
          </div>
          <div className="flex flex-wrap gap-4 items-center">
            <a href="tel:+38641350782" onClick={() => trackEvent("phone_click")} className="hover:text-primary flex items-center gap-1"><Phone className="h-3 w-3" />041 350 782</a>
            <a href="mailto:info@doberpogled.app" className="hover:text-primary">Kontakt</a>
            <Link to="/aplikacija" className="hover:text-primary" data-testid="footer-link-aplikacija">Mobilna aplikacija</Link>
            <Link to="/podpora" className="hover:text-primary" data-testid="footer-link-podpora">Podpora</Link>
            <Link to="/zasebnost" className="hover:text-primary" data-testid="footer-link-zasebnost">Zasebnost</Link>
            <Link to="/login" className="hover:text-primary">Prijava</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
